// Admin Dashboard.
//
// Three tabs, each gated by the viewer's capabilities (mirrors the backend
// RBAC):
//   - Users & roles  (ManageUsers / Admin) — list, invite, change role,
//     enable/disable. Drives the Cognito admin API.
//   - Pages          (ServerSettings / Admin) — per-instance page visibility
//     + role scoping. Always-on pages are locked.
//   - AI provider    (ServerSettings / Admin) — connect your own LLM. The key is
//     write-only: it is stored and never returned, so there is no reveal control
//     to add here, deliberately.
//   - Usage & Audit  (ViewAuditLog / Admin + Auditor) — data counts +
//     administrative audit log. Data operations live in the Database
//     Management page's Transaction log (audit kind=data), not here.
//
// Actions column on the LEFT throughout (CLAUDE.local.md design template).

import { Fragment, useEffect, useMemo, useState } from "react";
import { formatDuration } from '@/lib/time';
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { cn } from "@/lib/utils";
import { QueryLogTab } from "@/components/QueryLogTab";
import { VdbBadge } from "@/components/VdbBadge";
import {
  canExportStore,
  canManageAdmins,
  canManageUsers,
  canManageSettings,
  canViewAudit,
  useMe,
} from "@/lib/useMe";
import { SnapshotModal } from "@/components/SnapshotModal";
import {
  inviteUser,
  setPageVisibility,
  recoverUserAccess,
  setUserEnabled,
  setUserRole,
  useAdminPages,
  useAdminUsers,
  useAuditLog,
  useUsage,
  useUserActivity,
  exportAuditCsv,
  createServiceAccount,
  revokeServiceAccount,
  testServiceAccount,
  useServiceAccounts,
  type AgentTestResult,
  type AuditKind,
  type EffectivePage,
  useLlmConfig,
  setLlmConfig,
  clearLlmConfig,
  testLlmConfig,
  providerDisplayName,
  type ProviderTestResult,
  type Role,
} from "@/lib/useAdmin";
import { Message, messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { fieldClass } from '@/components/ui/field';
import { ServiceAccountKeyPanel } from '@/components/ServiceAccountKeyPanel';
import { ModelPicker } from '@/components/ModelPicker';
import {
  createDatabase,
  DATABASE_SCOPABLE_ROLES,
  deleteDatabase,
  renameDatabase,
  setDatabaseRoles,
  type DatabaseInfo,
} from '@/lib/api';
import { useActiveDatabase, useDatabases } from '@/lib/useDatabases';
const ROLES: Role[] = ["Owner", "Admin", "PowerUser", "User", "Auditor"];
/** Roles a page can be scoped to. The Owner is not scopable: the top seat
 *  sees every enabled page (a site owner locked out of a surface by their own
 *  page config is a defect, not a feature), and the API rejects "Owner" in
 *  allowed_roles. */
const SCOPABLE_ROLES: Role[] = ["Admin", "PowerUser", "User", "Auditor"];
/** The roles an Admin may hand out; Owner and Admin are an Owner's to give. */
const NON_ADMIN_ROLES: Role[] = ["PowerUser", "User", "Auditor"];

/** What a service account may act as, floor first. The server refuses a role
 *  above the creating administrator's own. */
const SERVICE_ACCOUNT_ROLES: { value: string; label: string; note: string }[] =
  [
    {
      value: "Integration",
      label: "Integration",
      note: "queries, taxonomy reads, result export and the assistant; writes nothing to the graph",
    },
    {
      value: "User",
      label: "User",
      note: "the same reads, plus visualization",
    },
    {
      value: "Auditor",
      label: "Auditor",
      note: "queries, the audit log and every user's questions; no edits",
    },
    {
      value: "PowerUser",
      label: "Power User",
      note: "loads, imports, edits, dashboard cards",
    },
    {
      value: "Admin",
      label: "Admin",
      note: "user management and server settings as well; asks you to confirm",
    },
  ];

type Tab =
  | "users"
  | "service-accounts"
  | "pages"
  | "databases"
  | "ai-provider"
  | "audit"
  | "query-log"
  | "activity";

export function AdminPage() {
  const me = useMe();
  const manageUsers = canManageUsers(me.data);
  const manageSettings = canManageSettings(me.data);
  const viewAudit = canViewAudit(me.data);

  const tabs = useMemo(() => {
    const t: { key: Tab; label: string }[] = [];
    if (manageUsers) t.push({ key: "users", label: "Users & roles" });
    if (manageUsers)
      t.push({ key: "service-accounts", label: "Service accounts" });
    if (manageSettings) t.push({ key: "pages", label: "Pages" });
    if (manageSettings) t.push({ key: "databases", label: "Databases" });
    if (manageSettings) t.push({ key: "ai-provider", label: "AI provider" });
    if (viewAudit) t.push({ key: "audit", label: "Usage & audit" });
    // The bodies live here, split from the audit (6.4). Server-enforced: the
    // Owner always; an Admin only under the Owner's grant — an ungranted
    // Admin sees the explanation, not a hidden tab.
    if (me.data?.role === "Owner" || me.data?.role === "Admin")
      t.push({ key: "query-log", label: "Query log" });
    if (viewAudit) t.push({ key: "activity", label: "User activity" });
    return t;
  }, [manageUsers, manageSettings, viewAudit, me.data?.role]);

  const [tab, setTab] = useState<Tab | null>(null);
  const active = tab ?? tabs[0]?.key ?? null;

  if (me.isLoading) {
    return (
      <div className="container mx-auto py-6 text-sm text-tres-text-secondary">
        Loading…
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-semibold text-tres-text-primary">Admin</h1>
        <p className="mt-2 text-sm text-tres-text-secondary">
          Your role doesn’t have administrative access.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="mb-1 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-tres-text-primary">Admin</h1>
        <VdbBadge />
      </div>
      <p className="mb-4 text-sm text-tres-text-secondary">
        Manage users, page visibility, and review usage &amp; activity for this
        instance.
      </p>

      <div className="mb-4 flex gap-1 overflow-x-auto whitespace-nowrap border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              active === t.key
                ? "border-tres-action text-tres-action"
                : "border-transparent text-tres-text-secondary hover:text-tres-text-primary",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {active === "users" && <UsersTab />}
      {active === "service-accounts" && <ServiceAccountsTab />}
      {active === "pages" && <PagesTab />}
      {active === "databases" && <DatabasesTab />}
      {active === "ai-provider" && <AiProviderTab />}
      {active === "audit" && <AuditTab />}
      {active === "query-log" && <QueryLogTab />}
      {active === "activity" && <ActivityTab />}
    </div>
  );
}

// ===============================================================
function UsersTab() {
  const meForScope = useMe();
  const ownerScope = canManageAdmins(meForScope.data);
  const roleOptions: Role[] = ownerScope ? ROLES : NON_ADMIN_ROLES;
  const isAdminRole = (r: Role | null | undefined) =>
    r === "Admin" || r === "Owner";
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const usersQuery = useAdminUsers(token, true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("User");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    // /api/me carries the viewer's own role and allowed_actions, so an admin
    // who changes their OWN role would keep the old capabilities — and the old
    // set of admin tabs — until that separate query happened to refetch.
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  }

  async function handleInvite() {
    if (!token) return;
    const email = inviteEmail.trim();
    if (!email || !email.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    const r = await inviteUser(email, inviteRole, token);
    if (r.ok) {
      setNotice(
        `Invited ${email} as ${inviteRole}. Cognito emailed a temporary password.`,
      );
      setInviteEmail("");
      refresh();
    } else {
      setError(r.message);
    }
    setBusy(false);
  }

  async function changeRole(username: string, role: Role) {
    if (!token) return;
    setError(null);
    const r = await setUserRole(username, role, token);
    if (r.ok) refresh();
    else setError(r.message);
  }

  async function toggleEnabled(username: string, enabled: boolean) {
    if (!token) return;
    setError(null);
    const r = await setUserEnabled(username, enabled, token);
    if (r.ok) refresh();
    else setError(r.message);
  }

  async function recoverAccess(username: string, status: string) {
    if (!token) return;
    setError(null);
    setNotice(null);
    const r = await recoverUserAccess(username, status, token);
    if (r.ok) {
      setNotice(
        status === "FORCE_CHANGE_PASSWORD"
          ? `Invitation re-sent to ${username} with a new temporary password.`
          : `Password reset forced for ${username} — Cognito emailed a reset code.`,
      );
    } else {
      setError(r.message);
    }
  }

  return (
    <div className="space-y-4">
      {/* Invite */}
      <div className="rounded-md border border-border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold text-tres-text-primary">
          Invite a user
        </h2>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
              Email
            </span>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={busy}
              placeholder="person@example.com"
              className="w-full rounded-md border border-border px-3 py-1.5 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
              Role
            </span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              disabled={busy}
              className="rounded-md border border-border px-3 py-1.5 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void handleInvite()}
            disabled={busy}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            {busy ? "Inviting…" : "Invite"}
          </button>
        </div>
        {notice && <p className="mt-2 text-xs text-tres-action">{notice}</p>}
      </div>

      {error && (
        <div className={messageClass('warning', 'normal')}>
          {error}
        </div>
      )}

      {/* Users table — actions column on the left */}
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-tres-text-secondary">
              <th className="px-3 py-2 font-semibold">Actions</th>
              <th className="px-3 py-2 font-semibold">User</th>
              <th className="px-3 py-2 font-semibold">Role</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-tres-text-secondary">
                  Loading users…
                </td>
              </tr>
            )}
            {usersQuery.isError && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-tres-error">
                  {(usersQuery.error as Error)?.message ??
                    "Failed to load users."}
                </td>
              </tr>
            )}
            {usersQuery.data?.map((u) => (
              <tr
                key={u.username}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => void toggleEnabled(u.username, !u.enabled)}
                      className={cn(
                        "rounded border px-2 py-0.5 text-xs font-medium",
                        u.enabled
                          ? "border-tres-error/40 text-tres-error hover:bg-tres-error/5"
                          : "border-tres-action/40 text-tres-action hover:bg-tres-submenu-bg",
                      )}
                    >
                      {u.enabled ? "Disable" : "Enable"}
                    </button>
                    {u.enabled && (
                      <button
                        type="button"
                        onClick={() => void recoverAccess(u.username, u.status)}
                        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                        title={
                          u.status === "FORCE_CHANGE_PASSWORD"
                            ? "Re-send the invitation email with a new temporary password"
                            : "Force a password reset — Cognito emails a reset code for the next sign-in"
                        }
                      >
                        {u.status === "FORCE_CHANGE_PASSWORD"
                          ? "Resend invite"
                          : "Reset password"}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-tres-text-primary">
                  {u.email ?? u.username}
                </td>
                <td className="px-3 py-2">
                  <select
                    value={u.role ?? ""}
                    onChange={(e) =>
                      void changeRole(u.username, e.target.value as Role)
                    }
                    disabled={!ownerScope && isAdminRole(u.role as Role | null)}
                    title={
                      !ownerScope && isAdminRole(u.role as Role | null)
                        ? "Only an Owner can change an Admin or an Owner"
                        : undefined
                    }
                    className="rounded border border-border px-2 py-0.5 text-xs focus:border-tres-action focus:outline-none disabled:opacity-50"
                  >
                    {!u.role && <option value="">(none)</option>}
                    {(isAdminRole(u.role as Role | null)
                      ? ROLES
                      : roleOptions
                    ).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-tres-text-secondary">
                  {u.enabled ? u.status : "DISABLED"}
                </td>
              </tr>
            ))}
            {usersQuery.isSuccess && usersQuery.data.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-tres-text-secondary">
                  No users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===============================================================
// Service accounts (machine credentials, Integration API)
// ===============================================================

function ServiceAccountsTab() {
  const me = useMe();
  const ownerScope = canManageAdmins(me.data);
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const listQuery = useServiceAccounts(token, true);
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [role, setRole] = useState("Integration");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freshKey, setFreshKey] = useState<{
    name: string;
    key: string;
    mcpPath: string | null;
  } | null>(null);

  function refresh() {
    void queryClient.invalidateQueries({
      queryKey: ["admin", "service-accounts"],
    });
  }

  async function handleCreate() {
    if (!token) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a name for the service account.");
      return;
    }
    if (
      role === "Admin" &&
      !window.confirm(
        `Give "${trimmed}" the Admin role? This key can invite and disable users, change server settings and run loads, from anywhere it is used, until it is revoked. The audit log will record it as an administrative machine key.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const r = await createServiceAccount(trimmed, purpose.trim(), role, token);
    if (r.ok) {
      setFreshKey({ name: trimmed, key: r.data.key, mcpPath: r.data.mcp_path ?? null });
      setName("");
      setPurpose("");
      setRole("Integration");
      refresh();
    } else {
      setError(r.message);
    }
    setBusy(false);
  }

  // One test at a time; each result stays under its own row until closed.
  const [testingId, setTestingId] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, AgentTestResult | { failed: string }>>({});

  async function handleTest(id: string) {
    if (!token) return;
    setTestingId(id);
    const r = await testServiceAccount(id, token);
    setTestingId(null);
    setTests((t) => ({
      ...t,
      [id]: r.ok ? r.data : { failed: r.message },
    }));
  }

  async function handleRevoke(id: string, accountName: string) {
    if (!token) return;
    if (
      !window.confirm(
        `Revoke "${accountName}"? Its key stops working on its next request. This cannot be undone.`,
      )
    ) {
      return;
    }
    setError(null);
    const r = await revokeServiceAccount(id, token);
    if (r.ok) refresh();
    else setError(r.message);
  }

  const atQuota =
    listQuery.data !== undefined &&
    listQuery.data.active >= listQuery.data.entitled;

  return (
    <div className="space-y-4">
      {!ownerScope && (
        <p className="rounded-md border border-border bg-card p-4 text-xs text-tres-text-secondary">
          Only an Owner can create or revoke a service account. You can see the
          accounts and when each key was last used.
        </p>
      )}
      {ownerScope && (
      <div className="rounded-md border border-border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold text-tres-text-primary">
          Create a service account
        </h2>
        <p className="mb-2 text-xs text-tres-text-secondary">
          A service account is a machine identity — for dashboards, BI tools,
          and AI assistants — that authenticates with a key instead of a
          sign-in. It acts with the role you give it: read-only by default, or
          the same role a person would hold when an assistant loads data or
          maintains cards on someone’s behalf. Everything it does is recorded
          under its name.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label>
            <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy || atQuota}
              placeholder="grafana-dashboards"
              maxLength={100}
              className="rounded-md border border-border px-3 py-1.5 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
            />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
              Purpose (optional)
            </span>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              disabled={busy || atQuota}
              placeholder="What will this credential be used for?"
              maxLength={500}
              className="w-full rounded-md border border-border px-3 py-1.5 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
              Role
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={busy || atQuota}
              className="rounded-md border border-border px-3 py-1.5 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
            >
              {SERVICE_ACCOUNT_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} — {r.note}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={busy || atQuota}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
        {listQuery.data && (
          <p className="mt-2 text-xs text-tres-text-secondary">
            {listQuery.data.active} of {listQuery.data.entitled} entitled
            service account
            {listQuery.data.entitled === 1 ? "" : "s"} in use.
            {atQuota &&
              " Revoke one to free a slot, or contact your account manager to add capacity."}
          </p>
        )}
      </div>
      )}

      {freshKey && (
        <ServiceAccountKeyPanel
          name={freshKey.name}
          secret={freshKey.key}
          mcpPath={freshKey.mcpPath}
          origin={window.location.origin}
          onDismiss={() => setFreshKey(null)}
        />
      )}

      {error && (
        <div className={messageClass('warning', 'normal')}>
          {error}
        </div>
      )}

      {/* Accounts table — actions column on the left */}
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-tres-text-secondary">
              <th className="px-3 py-2 font-semibold">Actions</th>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Role</th>
              <th className="px-3 py-2 font-semibold">Purpose</th>
              <th className="px-3 py-2 font-semibold">Created</th>
              <th className="px-3 py-2 font-semibold">Last used</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-tres-text-secondary">
                  Loading service accounts…
                </td>
              </tr>
            )}
            {listQuery.isError && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-tres-error">
                  {(listQuery.error as Error)?.message ??
                    "Failed to load service accounts."}
                </td>
              </tr>
            )}
            {listQuery.data?.accounts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-tres-text-secondary">
                  No service accounts yet.
                </td>
              </tr>
            )}
            {listQuery.data?.accounts.map((a) => {
              const result = tests[a.id];
              return (
              <Fragment key={a.id}>
              <tr className="border-b border-border last:border-b-0">
                <td className="space-x-2 px-3 py-2">
                  {a.revoked_at === null && (
                    <button
                      type="button"
                      disabled={testingId !== null}
                      onClick={() => void handleTest(a.id)}
                      className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                    >
                      {testingId === a.id ? "Testing…" : "Test"}
                    </button>
                  )}
                  {ownerScope && a.revoked_at === null && (
                    <button
                      type="button"
                      onClick={() => void handleRevoke(a.id, a.name)}
                      className={buttonVariants({ variant: 'danger', size: 'sm' })}
                    >
                      Revoke
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 font-medium text-tres-text-primary">
                  {a.name}
                </td>
                <td className="px-3 py-2 text-tres-text-secondary">{a.role}</td>
                <td className="px-3 py-2 text-tres-text-secondary">
                  {a.purpose || "—"}
                </td>
                <td className="px-3 py-2 text-tres-text-secondary">
                  {a.created_at}
                </td>
                <td className="px-3 py-2 text-tres-text-secondary">
                  {a.last_used_at ?? "never"}
                </td>
                <td className="px-3 py-2">
                  {a.revoked_at === null ? (
                    <span className="text-tres-action">active</span>
                  ) : (
                    <span className="text-tres-text-secondary">
                      revoked {a.revoked_at}
                    </span>
                  )}
                </td>
              </tr>
              {testingId === a.id && (
                <tr className="border-b border-border">
                  <td colSpan={7} className="px-3 py-2 text-sm text-tres-text-secondary" role="status">
                    Asking one question as {a.name}…
                  </td>
                </tr>
              )}
              {result && testingId !== a.id && (
                <tr className="border-b border-border">
                  <td colSpan={7} className="px-3 py-3">
                    <AgentTestReport
                      name={a.name}
                      result={result}
                      onClose={() =>
                        setTests((t) => {
                          const next = { ...t };
                          delete next[a.id];
                          return next;
                        })
                      }
                    />
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** What an agent test found, under the agent's row: each check a call from
 *  the agent meets, then the answer it got. */
function AgentTestReport({
  name,
  result,
  onClose,
}: {
  name: string;
  result: AgentTestResult | { failed: string };
  onClose: () => void;
}) {
  return (
    <div className="space-y-2 text-sm">
      {"failed" in result ? (
        <Message kind="warning" density="compact">
          The test could not run: {result.failed}
        </Message>
      ) : (
        <>
          <ul className="space-y-1">
            {result.checks.map((c) => (
              <li key={c.check} className={c.ok ? "text-tres-text-primary" : "text-tres-error"}>
                <span className="font-medium">{c.ok ? "Passed" : "Failed"}:</span> {c.check}.{" "}
                <span className={c.ok ? "text-tres-text-secondary" : undefined}>{c.detail}</span>
              </li>
            ))}
          </ul>
          {result.ok ? (
            <Message kind="outcome" density="compact">
              {name} asked: <em>{result.question}</em>
              <br />
              {result.answer}
            </Message>
          ) : (
            <Message kind="warning" density="compact">
              A call from {name} would be refused at the first failed check above.
            </Message>
          )}
          <p className="text-xs text-tres-text-secondary">
            This asks as {name} from inside the site. It cannot test the agent's key or the network
            path from where the agent runs: if this passes and the agent still fails, check the key it
            was given and the address it calls.
          </p>
        </>
      )}
      <button
        type="button"
        onClick={onClose}
        className={buttonVariants({ variant: 'ghost', size: 'sm' })}
      >
        Close
      </button>
    </div>
  );
}

// ===============================================================
// Pages
// ===============================================================

function PagesTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const pagesQuery = useAdminPages(token, true);
  const [error, setError] = useState<string | null>(null);

  async function save(page: EffectivePage, enabled: boolean, allowed: Role[]) {
    if (!token) return;
    setError(null);
    const r = await setPageVisibility(
      { page_key: page.key, enabled, allowed_roles: allowed },
      token,
    );
    if (r.ok) {
      void queryClient.invalidateQueries({ queryKey: ["admin", "pages"] });
      void queryClient.invalidateQueries({ queryKey: ["pages-config"] });
    } else {
      setError(r.message);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-tres-text-secondary">
        Signed-out visitors see what <span className="font-medium">User</span>{" "}
        sees — scope a page to User to show it to them, or away from User to
        hide it. Turn secondary pages on or off, or scope them to specific
        roles. Always-on pages (Query, Viz, Data, Preferences, Welcome) can’t be
        disabled. Hiding a page is a convenience — its data API still enforces
        its own permissions.
      </p>
      {error && (
        <div className={messageClass('warning', 'normal')}>
          {error}
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-tres-text-secondary">
              <th className="px-3 py-2 font-semibold">Enabled</th>
              <th className="px-3 py-2 font-semibold">Page</th>
              <th className="px-3 py-2 font-semibold">Visible to roles</th>
            </tr>
          </thead>
          <tbody>
            {pagesQuery.isLoading && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-tres-text-secondary">
                  Loading…
                </td>
              </tr>
            )}
            {pagesQuery.data?.map((p) => (
              <tr
                key={p.key}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={p.enabled}
                    disabled={p.always_on}
                    title={
                      p.always_on
                        ? "Always-on page — cannot be disabled"
                        : undefined
                    }
                    onChange={(e) =>
                      void save(p, e.target.checked, p.allowed_roles)
                    }
                    className="h-4 w-4 accent-tres-action disabled:opacity-50"
                  />
                </td>
                <td className="px-3 py-2 text-tres-text-primary">
                  {prettyPage(p.key)}
                  {p.always_on && (
                    <span className="ml-2 rounded bg-muted px-1 text-[10px] uppercase text-tres-text-secondary">
                      always on
                    </span>
                  )}
                  {p.mobile && (
                    <span
                      className="ml-2 rounded bg-tres-action/10 px-1 text-[10px] uppercase text-tres-action"
                      title="Offered on phone-sized screens. Fixed per page — pages that need a large canvas never are."
                    >
                      mobile
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {p.always_on ? (
                    <span className="text-xs text-tres-text-secondary">
                      All
                    </span>
                  ) : (
                    <RoleScope
                      selected={p.allowed_roles}
                      disabled={!p.enabled}
                      onChange={(roles) => void save(p, p.enabled, roles)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Role-scope picker: empty = all roles, else the checked subset. */
function RoleScope({
  selected,
  disabled,
  onChange,
}: {
  selected: Role[];
  disabled: boolean;
  onChange: (roles: Role[]) => void;
}) {
  function toggle(role: Role) {
    const next = selected.includes(role)
      ? selected.filter((r) => r !== role)
      : [...selected, role];
    onChange(next);
  }
  const allRoles = selected.length === 0;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* An explicit All, because the stored form of "every role" is an EMPTY
          list: without this the admin had to UNCHECK everything to widen a
          page, which reads backwards and looks like they scoped it to nobody. */}
      <label className="inline-flex items-center gap-1 text-xs font-medium text-tres-text-primary">
        <input
          type="checkbox"
          checked={allRoles}
          disabled={disabled}
          onChange={() => onChange(allRoles ? [...SCOPABLE_ROLES] : [])}
          className="h-3.5 w-3.5 accent-tres-action disabled:opacity-50"
        />
        All
      </label>
      <span aria-hidden className="h-3 w-px bg-border" />
      {SCOPABLE_ROLES.map((r) => (
        <label
          key={r}
          className="inline-flex items-center gap-1 text-xs text-tres-text-primary"
        >
          <input
            type="checkbox"
            checked={selected.includes(r)}
            disabled={disabled}
            onChange={() => toggle(r)}
            className="h-3.5 w-3.5 accent-tres-action disabled:opacity-50"
          />
          {r}
        </label>
      ))}
    </div>
  );
}

// ===============================================================
// Usage & audit
// ===============================================================

const AUDIT_FILTERS: { value: AuditKind | undefined; label: string }[] = [
  { value: undefined, label: "All" },
  { value: "admin", label: "Admin console" },
  { value: "data", label: "Data operations" },
  { value: "knowledge", label: "Knowledge edits" },
  { value: "llm", label: "AI questions" },
  { value: "query", label: "Queries" },
];

function AuditTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  // The tab is named "Usage & audit" — the usage half was fetched by a hook
  // nobody called while only the audit table rendered (2026-08-06 audit D8).
  const usage = useUsage(token, true);
  const [kind, setKind] = useState<AuditKind | undefined>(undefined);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const auditQuery = useAuditLog(token, true, kind);

  const doExport = async () => {
    if (!token) return;
    setExporting(true);
    setExportError(null);
    const r = await exportAuditCsv(token, kind);
    setExporting(false);
    if (!r.ok) {
      setExportError(r.message);
      return;
    }
    const blob = new Blob([r.text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = r.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Usage — the tab's first promise */}
      {usage.data && (
        <div className="flex flex-wrap gap-6 rounded-md border border-border bg-card px-4 py-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-tres-text-secondary">
              Statements
            </div>
            <div className="font-semibold">
              {usage.data.triples.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-tres-text-secondary">
              Graphs
            </div>
            <div className="font-semibold">
              {usage.data.graphs.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-tres-text-secondary">
              Backend
            </div>
            <div className="font-semibold uppercase">{usage.data.backend}</div>
          </div>
        </div>
      )}

      {/* Audit */}
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
            Recent activity
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {AUDIT_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => setKind(f.value)}
                className={
                  kind === f.value
                    ? "rounded-full bg-tres-action px-2.5 py-0.5 text-xs font-medium text-white"
                    : "rounded-full border border-border px-2.5 py-0.5 text-xs text-tres-text-secondary hover:text-tres-text-primary"
                }
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => void doExport()}
              disabled={exporting}
              title="Download this view's full trail as CSV"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'ml-2')}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            {exportError && (
              <span role="alert" className="text-xs text-destructive">
                {exportError}
              </span>
            )}
          </div>
        </div>
        {auditQuery.data && !auditQuery.data.available && (
          <p className="px-3 py-3 text-sm text-tres-text-secondary">
            Audit storage isn’t configured on this instance (no state
            directory).
          </p>
        )}
        {auditQuery.data?.available && auditQuery.data.entries.length === 0 && (
          <p className="px-3 py-3 text-sm text-tres-text-secondary">
            No activity recorded yet.
          </p>
        )}
        {auditQuery.data && auditQuery.data.entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-tres-text-secondary">
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Actor</th>
                  <th className="px-3 py-2 font-semibold">Action</th>
                  <th className="px-3 py-2 font-semibold">Target</th>
                  <th className="px-3 py-2 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {auditQuery.data.entries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="whitespace-nowrap px-3 py-1.5 text-tres-text-secondary">
                      {e.ts}
                    </td>
                    <td className="px-3 py-1.5 text-tres-text-primary">
                      {e.actor_email ?? e.actor_name ?? e.actor}
                      {e.actor_kind === "service_account" && (
                        <span className="ml-1 rounded bg-muted px-1 text-[10px] uppercase tracking-wide text-tres-text-secondary">
                          service account
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-tres-text-primary">
                      {e.action}
                    </td>
                    <td className="px-3 py-1.5 text-tres-text-secondary">
                      {e.target ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-tres-text-secondary">
                      {e.detail ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===============================================================
// User activity — logins + time on site
// ===============================================================

function ActivityTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const activityQuery = useUserActivity(token, true);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
          Logins &amp; time on site
        </div>
        {activityQuery.data && !activityQuery.data.available && (
          <p className="px-3 py-3 text-sm text-tres-text-secondary">
            Activity tracking isn’t configured on this instance (no state
            directory).
          </p>
        )}
        {activityQuery.data?.available &&
          activityQuery.data.entries.length === 0 && (
            <p className="px-3 py-3 text-sm text-tres-text-secondary">
              No activity recorded yet.
            </p>
          )}
        {activityQuery.data && activityQuery.data.entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-tres-text-secondary">
                  <th className="px-3 py-2 font-semibold">User</th>
                  <th className="px-3 py-2 font-semibold">Logins</th>
                  <th className="px-3 py-2 font-semibold">Time on site</th>
                  <th className="px-3 py-2 font-semibold">First seen</th>
                  <th className="px-3 py-2 font-semibold">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {activityQuery.data.entries.map((e) => (
                  <tr
                    key={e.user_id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-3 py-1.5 text-tres-text-primary">
                      {e.email ?? e.user_id}
                    </td>
                    <td className="px-3 py-1.5 text-tres-text-primary">
                      {e.logins}
                    </td>
                    <td className="px-3 py-1.5 text-tres-text-primary">
                      {formatDuration(e.active_seconds)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-tres-text-secondary">
                      {e.first_seen}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-tres-text-secondary">
                      {e.last_seen}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-tres-text-secondary">
        Each sign-in is one login. Time on site is the span from a session’s
        first to last request, summed across sessions — it measures active
        windows, not idle time in an open tab.
      </p>
    </div>
  );
}

const PAGE_LABELS: Record<string, string> = {
  welcome: "Welcome",
  dashboard: "Dashboard",
  timeline: "Timeline",
  query: "Query",
  browse: "Browse",
  viz: "Viz",
  vocabulary: "Vocabulary",
  ontology: "Ontology",
  catalog: "Catalog",
  entity: "Entity",
  trainer: "LLM Testbed",
  rules: "Rules",
  snippets: "Snippets",
  preferences: "Preferences",
  "admin-load": "Data Management",
};

function prettyPage(key: string): string {
  return PAGE_LABELS[key] ?? key;
}

// ===============================================================
// AI provider (BYO-LLM)
// ===============================================================

function AiProviderTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const cfg = useLlmConfig(token, true);

  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [endpoint, setEndpoint] = useState("");
  // Empty means "leave the stored key alone". There is no way to populate this
  // from the server — see the note in useAdmin.ts.
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  // Transient "Saved" confirmation beside the button. Cleared on the next edit
  // so it can never describe a state the form has since moved away from.
  const [justSaved, setJustSaved] = useState(false);

  const data = cfg.data;
  const providers = data?.supported_providers ?? ["ollama", "claude", "openai"];
  const effectiveProvider = provider || data?.provider || providers[0] || "";
  // Settings and key status belong to the provider CHOSEN IN THIS FORM, not to
  // the instance. Switching provider keeps every provider's key, so reading
  // `data.key_configured` here would report the selected provider's key while
  // the form showed another (checklist 20.7).
  const chosen = data?.providers?.find((p) => p.provider === effectiveProvider);
  const effectiveModel = model || chosen?.model || "";
  const effectiveEndpoint = endpoint || chosen?.endpoint || "";
  const chosenHasKey = chosen?.key_configured ?? false;
  const providersWithKeys = (data?.providers ?? []).filter((p) => p.key_configured);

  // A test describes the settings it ran with; once any of them changes, the
  // result no longer describes what the form shows, so it goes.
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ProviderTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  useEffect(() => {
    setTestResult(null);
    setTestError(null);
  }, [effectiveProvider, effectiveModel, effectiveEndpoint, apiKey]);

  async function testConnection() {
    if (!token) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    const r = await testLlmConfig(
      {
        provider: effectiveProvider,
        model: effectiveModel,
        endpoint: effectiveEndpoint || undefined,
        api_key: apiKey || undefined,
      },
      token,
    );
    setTesting(false);
    if (r.ok) setTestResult(r.data);
    else setTestError(r.message);
  }

  async function save() {
    if (!token) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    const r = await setLlmConfig(
      {
        provider: effectiveProvider,
        model: effectiveModel,
        endpoint: effectiveEndpoint || undefined,
        api_key: apiKey || undefined,
      },
      token,
    );
    setBusy(false);
    if (r.ok) {
      setApiKey("");
      setNotice("Saved. New questions use this provider immediately.");
      // Confirmation next to the button as well as at the top of the panel.
      // The button is at the bottom of a long form, so a notice rendered only
      // at the top lands off-screen and the save reads as having done nothing.
      setJustSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["admin", "llm-config"] });
      // /api/me carries the serving model for the Evaluator page, and it is a
      // SEPARATE cached query — without this it keeps showing the previous model
      // after a successful save, which reads as the save not having worked.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    } else {
      setError(r.message);
    }
  }

  async function remove() {
    if (!token) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    const target = effectiveProvider;
    const r = await clearLlmConfig(token, target);
    setBusy(false);
    setConfirmRemove(false);
    if (r.ok) {
      setProvider("");
      setModel("");
      setEndpoint("");
      setApiKey("");
      // Name what was removed and what is serving now. Removing one provider
      // leaves the others' keys where they were, so "back on the default" is
      // only true when nothing else is configured.
      setNotice(
        `Removed ${target}, including its stored key. Now serving: ${r.data?.reverted_to ?? "the instance default"}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["admin", "llm-config"] });
      // /api/me carries the serving model for the Evaluator page, and it is a
      // SEPARATE cached query — without this it keeps showing the previous model
      // after a successful save, which reads as the save not having worked.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    } else {
      setError(r.message);
    }
  }

  if (cfg.isLoading) {
    return <div className="text-sm text-tres-text-secondary">Loading…</div>;
  }

  // A stored configuration that did not load leaves AI off rather than silently
  // falling back, so say so plainly — otherwise the page shows a saved provider
  // while chat returns 503 and nothing connects the two.
  const configuredButInactive =
    !!data?.customer_configured && !data?.active_provider;

  return (
    <div className="space-y-4">
      <p className="text-sm text-tres-text-secondary">
        Connect your own AI provider. Your key is stored in this instance’s
        private credential store and is never shown again — not to you, and not
        to support. To change it, enter a new one. Leaving your own provider
        connected means no AI add-on fee.
      </p>

      {error && (
        <div className={messageClass('warning', 'normal')}>
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded border border-border bg-muted px-3 py-2 text-sm text-tres-text-secondary">
          {notice}
        </div>
      )}
      {configuredButInactive && (
        <div className={messageClass('warning', 'normal')}>
          A provider is saved but did not load, so AI features are switched off.
          Check the model name and key, then save again.
        </div>
      )}
      {data && !data.secret_storage_available && (
        <div className="rounded border border-border bg-muted px-3 py-2 text-sm text-tres-text-secondary">
          This deployment has no credential store, so a key cannot be saved
          here. A provider that needs no key, such as self-hosted Ollama, still
          works.
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-border bg-card p-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-tres-text-secondary">Currently serving</dt>
          <dd>
            {data?.active_provider
              ? `${providerDisplayName(data.active_provider)} · ${data.active_model ?? ""}`
              : "AI features are off"}
          </dd>
          <dt className="text-tres-text-secondary">Source</dt>
          <dd>
            {data?.customer_configured ? "Your provider" : "Instance default"}
          </dd>
          {/* Whose key: this row reports the CUSTOMER's key only — never the
              instance default, which lives in a separate secret this surface
              never reads. Labelled "Your key" because a bare "Key: Not stored"
              sitting under "Currently serving: <model>" reads as a
              contradiction, and "not stored" is the correct resting state
              until someone attaches their own provider. */}
          <dt className="text-tres-text-secondary">Your keys</dt>
          <dd>
            {providersWithKeys.length > 0
              ? `Stored for ${providersWithKeys.map((p) => providerDisplayName(p.provider)).join(", ")}`
              : data?.customer_configured
                ? "Not stored — your provider is configured without one"
                : "Not stored — the instance default is serving"}
          </dd>
          {data?.updated_by && (
            <>
              <dt className="text-tres-text-secondary">Last changed</dt>
              <dd>
                {data.updated_by}
                {data.updated_at ? ` · ${data.updated_at}` : ""}
              </dd>
            </>
          )}
        </dl>
      </div>

      <div className="space-y-3 overflow-hidden rounded-md border border-border bg-card p-4">
        <label className="block text-sm">
          <span className="text-tres-text-secondary">Provider</span>
          <select
            className="mt-1 w-full rounded border border-border px-2 py-1"
            value={effectiveProvider}
            onChange={(e) => {
              setProvider(e.target.value);
              // Show the newly chosen provider's own stored model and endpoint
              // rather than carrying the previous provider's across.
              setModel("");
              setEndpoint("");
              setApiKey("");
              setJustSaved(false);
            }}
          >
            {providers.map((p) => {
              const stored = data?.providers?.find((s) => s.provider === p);
              return (
                <option key={p} value={p}>
                  {providerDisplayName(p)}
                  {stored?.key_configured ? " — key stored" : ""}
                </option>
              );
            })}
          </select>
        </label>

        <ModelPicker
          provider={effectiveProvider}
          endpoint={effectiveEndpoint}
          apiKey={apiKey}
          token={token}
          value={effectiveModel}
          onChange={(m) => {
            setModel(m);
            setJustSaved(false);
          }}
        />

        <label className="block text-sm">
          <span className="text-tres-text-secondary">
            Endpoint{effectiveProvider === "ollama" ? "" : " (optional)"}
          </span>
          <input
            className="mt-1 w-full rounded border border-border px-2 py-1"
            value={effectiveEndpoint}
            onChange={(e) => {
              setEndpoint(e.target.value);
              setJustSaved(false);
            }}
            placeholder={
              effectiveProvider === "ollama"
                ? "e.g. https://ollama.com — required, there is no usable default here"
                : "Leave blank for the provider default"
            }
          />
          {/* Ollama's built-in default is localhost, which nothing serves in
              this deployment — a blank endpoint saved cleanly and then refused
              every question with "connection refused". The server rejects it
              now; saying so here means the admin does not have to discover it
              by failing. Hosted providers keep a correct built-in default, so
              the field stays genuinely optional for them. */}
          {effectiveProvider === "ollama" && (
            <span className="mt-1 block text-xs text-tres-text-secondary">
              Ollama has no usable default address on this deployment — its
              default is the server’s own machine, where nothing is running.
            </span>
          )}
        </label>

        <p className="text-xs text-tres-text-secondary">
          Changing the model changes how every AI-assisted feature behaves — chat
          answers, document extraction, import proposals. Results are verified on
          the on-board model; another model may do better or worse, so re-check the
          answers you rely on after a switch.
        </p>

        <label className="block text-sm">
          <span className="text-tres-text-secondary">
            API key
            {/* State it AT the field. The card above and the paragraph above
                that both describe key storage, and neither is where someone
                looks when deciding whether they have to re-type a key to
                change a model. */}
            {chosenHasKey && (
              <span className="ml-2 font-normal text-tres-action">
                A key is stored for {providerDisplayName(effectiveProvider)}
                {chosen?.key_set_at ? ` (set ${chosen.key_set_at})` : ""} —
                leave blank to keep it
              </span>
            )}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            className="mt-1 w-full rounded border border-border px-2 py-1 disabled:bg-muted"
            value={apiKey}
            disabled={!data?.secret_storage_available}
            onChange={(e) => {
              setApiKey(e.target.value);
              setJustSaved(false);
            }}
            placeholder={
              data?.secret_storage_available
                ? chosenHasKey
                  ? `A key is stored for ${providerDisplayName(effectiveProvider)} — enter a new one to replace it`
                  : `Paste your ${providerDisplayName(effectiveProvider)} key`
                : "Unavailable on this deployment"
            }
          />
          {/* The reason belongs AT the disabled control. It was stated only in
              a banner at the top of the panel, which on a long form is nowhere
              near the field it explains. Provider and model still save — a
              self-hosted provider needs no key — so the form stays usable. */}
          {data && !data.secret_storage_available && (
            <span className="mt-1 block text-xs text-tres-text-secondary">
              No credential store on this deployment, so a key cannot be saved.
              Provider and model still save, and a provider that needs no key
              still works.
            </span>
          )}
        </label>

        {/* Actions on the LEFT (design template). */}
        <div className="flex items-center gap-2 pt-1">
          <button
            className={buttonVariants({ variant: 'primary', size: 'md' })}
            disabled={busy || !effectiveModel}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : "Save"}
          </button>
          {justSaved && !busy && (
            <span className="text-sm text-tres-action" role="status">
              Saved — new questions use this provider immediately.
            </span>
          )}
          <button
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
            disabled={busy || testing || !effectiveModel}
            onClick={() => void testConnection()}
          >
            {testing ? "Testing…" : "Test connection"}
          </button>
          {chosen && !confirmRemove && (
            <button
              className={buttonVariants({ variant: 'secondary', size: 'md' })}
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              Remove {providerDisplayName(effectiveProvider)}
            </button>
          )}
          {confirmRemove && (
            <>
              {/* Name the provider on the destructive control itself. A key
                  cannot be read back, so removing the wrong one costs the
                  customer a trip to their provider for a new key. */}
              <button
                className={buttonVariants({ variant: 'danger', size: 'md' })}
                disabled={busy}
                onClick={() => void remove()}
              >
                Remove {providerDisplayName(effectiveProvider)}
                {chosenHasKey ? " and its stored key" : ""}
              </button>
              <button
                className={buttonVariants({ variant: 'secondary', size: 'md' })}
                onClick={() => setConfirmRemove(false)}
              >
                Cancel
              </button>
            </>
          )}
        </div>
        {/* The answer to Test connection, under the button that asked. It
            tests the form as it stands, saved or not, and changes nothing. */}
        {testing && (
          <p className="text-sm text-tres-text-secondary" role="status">
            Sending a short prompt to {providerDisplayName(effectiveProvider)} · {effectiveModel}…
          </p>
        )}
        {testResult?.ok && (
          <Message kind="outcome" density="compact">
            {providerDisplayName(testResult.provider)} answered in {((testResult.elapsed_ms ?? 0) / 1000).toFixed(1)} s
            {testResult.reported_model ? ` as ${testResult.reported_model}` : ` (${testResult.model})`}:{" "}
            <em>{testResult.reply || "(an empty reply)"}</em>
            {!chosen?.selected || effectiveModel !== chosen?.model || apiKey
              ? " These settings are not saved yet; Save to switch to them."
              : ""}
          </Message>
        )}
        {testResult && !testResult.ok && (
          <Message kind="warning" density="compact">
            {providerDisplayName(testResult.provider)} · {testResult.model} did not answer: {testResult.error}
          </Message>
        )}
        {testError && (
          <Message kind="warning" density="compact">
            The test could not run: {testError}
          </Message>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Databases (36.12): the stores this site holds, one index each.

/** Who may use a database, in words: every role, or the ones named. */
function whoMayUse(allowed: string[]): string {
  if (allowed.length === 0) return "every role";
  const named = DATABASE_SCOPABLE_ROLES.filter((r) => allowed.includes(r.role)).map((r) => r.label);
  return `Owner, Admin, ${named.join(", ")}`;
}

function DatabasesTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const databases = useDatabases();
  const me = useMe();
  const [snapshotOf, setSnapshotOf] = useState<DatabaseInfo | null>(null);
  const { active, switchTo } = useActiveDatabase();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<DatabaseInfo | null>(null);
  const [scoping, setScoping] = useState<{ db: DatabaseInfo; roles: string[] } | null>(null);

  const list = databases.data;
  const count = list?.databases.length ?? 0;
  const cap = list?.max_databases ?? 0;
  const atCap = list ? count >= cap : false;
  const idOk = /^[a-z][a-z0-9-]{0,31}$/.test(id) && !id.endsWith("-");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["databases"] });
  };

  async function create() {
    if (!token || !idOk) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    const trimmed = name.trim();
    const r = await createDatabase(trimmed ? { id, name: trimmed } : { id }, token);
    setBusy(false);
    if (r.ok) {
      setOutcome(`Created ${r.data.name} (${r.data.id}); its index is building. Switch to it on Data Management to use it.`);
      setId("");
      setName("");
      refresh();
    } else {
      setError(r.message);
    }
  }

  async function saveRename() {
    if (!token || !renaming) return;
    setBusy(true);
    setError(null);
    const r = await renameDatabase(renaming.id, renaming.name, token);
    setBusy(false);
    if (r.ok) {
      setRenaming(null);
      refresh();
    } else {
      setError(r.message);
    }
  }

  async function saveRoles() {
    if (!token || !scoping) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    const r = await setDatabaseRoles(scoping.db.id, scoping.roles, token);
    setBusy(false);
    if (r.ok) {
      setOutcome(
        `${scoping.db.name} is now used by ${whoMayUse(r.data.allowed_roles)}. Anyone else who has it picked is moved to the default database on their next page.`,
      );
      setScoping(null);
      refresh();
    } else {
      setError(r.detail ? `${r.error}: ${r.detail}` : r.error);
    }
  }

  async function confirmRemove() {
    if (!token || !removing) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    const r = await deleteDatabase(removing.id, token);
    setBusy(false);
    if (r.ok) {
      if (active === removing.id) switchTo(null);
      setOutcome(
        `Removed ${removing.name} (${removing.id}): ${r.data.statements.toLocaleString()} statements and its index are gone.`,
      );
      setRemoving(null);
      refresh();
    } else {
      setError(r.message);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-tres-text-secondary">
        A database is a store of its own with its own index: a test set stays loaded beside the
        working set; the header names the one every page reads, and switching is a step on Data Management. This site may
        hold {cap} database{cap === 1 ? "" : "s"}, the default counted; it holds {count}.
      </p>
      {error && <div className={messageClass("warning", "normal")}>{error}</div>}
      {outcome && <div className={messageClass("outcome", "normal")}>{outcome}</div>}
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="tres-table min-w-max" aria-label="Databases">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Name</th>
              <th>Id</th>
              <th>Who may use it</th>
            </tr>
          </thead>
          <tbody>
            {databases.isLoading && (
              <tr>
                <td colSpan={4} className="text-tres-text-secondary">
                  Loading…
                </td>
              </tr>
            )}
            {list?.databases.map((d) => (
              <tr key={d.id}>
                <td>
                  <div className="flex gap-2">
                    {canExportStore(me.data) && (
                      <button
                        type="button"
                        className={buttonVariants({ variant: "secondary", size: "sm" })}
                        disabled={busy}
                        onClick={() => setSnapshotOf(d)}
                      >
                        Snapshot
                      </button>
                    )}
                    <button
                      type="button"
                      className={buttonVariants({ variant: "secondary", size: "sm" })}
                      disabled={busy}
                      onClick={() => setRenaming({ id: d.id, name: d.name })}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className={buttonVariants({ variant: "secondary", size: "sm" })}
                      disabled={busy}
                      onClick={() => setScoping({ db: d, roles: d.allowed_roles })}
                    >
                      Who may use it
                    </button>
                    <button
                      type="button"
                      className={buttonVariants({ variant: "danger", size: "sm" })}
                      disabled={d.default || busy}
                      title={d.default ? "The default database stays; it is the one the site starts with." : undefined}
                      onClick={() => setRemoving(d)}
                    >
                      Remove
                    </button>
                  </div>
                </td>
                <td className="font-medium">
                  {d.name}
                  {(active ?? list.default) === d.id && (
                    <span className="ml-2 rounded bg-tres-status-active/15 px-1.5 py-0.5 text-xs font-medium text-tres-status-active">
                      In use
                    </span>
                  )}
                  {d.default && <span className="ml-2 text-xs text-tres-text-secondary">default</span>}
                </td>
                <td className="font-mono text-xs">{d.id}</td>
                <td className="text-sm">{whoMayUse(d.allowed_roles)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list?.can_create ? (
        <div className="rounded-md border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold">Create a database</h3>
          {atCap ? (
            <div className={messageClass("guidance", "normal")}>
              This site is at its limit of {cap} database{cap === 1 ? "" : "s"}. Remove one to
              create another, or ask about a higher limit.
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span>Id</span>
                <input
                  className={fieldClass(id && !idOk ? "error" : "normal")}
                  value={id}
                  onChange={(e) => setId(e.target.value.trim().toLowerCase())}
                  placeholder="sandbox"
                  disabled={busy}
                />
                <span className="text-xs text-tres-text-secondary">
                  Lower-case letters, digits and hyphens; it names the store's directory.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>Name</span>
                <input
                  className={fieldClass("normal")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sandbox"
                  disabled={busy}
                />
              </label>
              <button
                type="button"
                className={buttonVariants({ variant: "primary", size: "md" })}
                disabled={!idOk || busy}
                onClick={() => void create()}
              >
                Create
              </button>
            </div>
          )}
        </div>
      ) : (
        list && (
          <div className={messageClass("guidance", "normal")}>
            This site runs the RDF line, which holds one database.
          </div>
        )
      )}
      {renaming && (
        <Modal
          title="Rename database"
          subtitle={`The id ${renaming.id} stays; only the name shown changes.`}
          onClose={() => setRenaming(null)}
          busy={busy}
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonVariants({ variant: "secondary", size: "md" })}
                onClick={() => setRenaming(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={buttonVariants({ variant: "primary", size: "md" })}
                onClick={() => void saveRename()}
                disabled={busy || !renaming.name.trim()}
              >
                Save
              </button>
            </div>
          }
        >
          <input
            className={fieldClass("normal")}
            value={renaming.name}
            onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
            disabled={busy}
          />
        </Modal>
      )}
      {scoping && (
        <Modal
          title={`Who may use ${scoping.db.name}`}
          subtitle="The Owner and every Admin always may. Leave every box clear for every role, as before."
          onClose={() => setScoping(null)}
          busy={busy}
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonVariants({ variant: "secondary", size: "md" })}
                onClick={() => setScoping(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={buttonVariants({ variant: "primary", size: "md" })}
                onClick={() => void saveRoles()}
                disabled={busy}
              >
                Save
              </button>
            </div>
          }
        >
          <fieldset className="space-y-2">
            <legend className="sr-only">Roles that may use the database</legend>
            {DATABASE_SCOPABLE_ROLES.map(({ role, label }) => (
              <label key={role} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scoping.roles.includes(role)}
                  disabled={busy}
                  onChange={(e) =>
                    setScoping({
                      ...scoping,
                      roles: e.target.checked
                        ? [...scoping.roles, role]
                        : scoping.roles.filter((r) => r !== role),
                    })
                  }
                />
                {label}
              </label>
            ))}
          </fieldset>
          <p className="mt-3 text-xs text-tres-text-secondary">
            A role left out does not see the database in the header and is refused if it names it,
            machine keys and guests included.
          </p>
        </Modal>
      )}
      {snapshotOf && (
        <SnapshotModal
          databaseId={snapshotOf.id}
          databaseName={snapshotOf.name}
          onClose={() => setSnapshotOf(null)}
        />
      )}
      {removing && (
        <Modal
          title={`Remove ${removing.name}?`}
          subtitle="Its store, index and every statement in it are deleted, with its question banks, asked questions, layouts, corrections and other records. This cannot be undone."
          onClose={() => setRemoving(null)}
          busy={busy}
          size="md"
          danger
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={buttonVariants({ variant: "secondary", size: "md" })}
                onClick={() => setRemoving(null)}
                disabled={busy}
              >
                Keep it
              </button>
              <button
                type="button"
                className={buttonVariants({ variant: "danger", size: "md" })}
                onClick={() => void confirmRemove()}
                disabled={busy}
              >
                Remove the database
              </button>
            </div>
          }
        >
          <p className="text-sm">
            Database <span className="font-mono">{removing.id}</span>. Download a snapshot first if
            anything in it is wanted later.
          </p>
        </Modal>
      )}
    </div>
  );
}
